import React, { useState, useEffect, useMemo } from 'react';
import { BookOpen, Target, Flame, Book, Calendar, ChevronRight, TrendingUp, Moon, Timer, Eye, Headphones, Heart, Music, Repeat } from 'lucide-react';
import { useProfileCredits } from '@/hooks/use-profile-credits';
import { useStableAvatar } from '@/hooks/use-stable-avatar';
import { ReadingJourneyModal } from '@/components/ReadingJourneyModal';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { useAppConfig } from '@/hooks/use-app-config';
import { useAuth } from '@/components/AuthProvider';
import { useProfileReadingData } from '@/hooks/use-profile-reading-data';
import { useBookAvatars } from '@/hooks/use-book-avatars';
import { useCombinedListeningStats } from '@/hooks/use-track-listening';
import confetti from 'canvas-confetti';
import { useToast } from '@/hooks/use-toast';

interface ReadingJourneyStoriesProps {
  currentStreak: number;
  totalBooksRead: number;
  readingGoal: number;
  booksThisMonth?: number;
  isLoading?: boolean;
  isError?: boolean;
  refetchData?: () => void;
  onOpenAuthModal?: () => void;
  updateReadingGoal?: (newGoal: number) => void;
  isUpdatingGoal?: boolean;
}

interface StoryItemProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  gradientFrom: string;
  gradientTo: string;
  ringColor: string;
  onClick: () => void;
  hasProgress?: boolean;
  progressPercent?: number;
  isComplete?: boolean;
  avatarUrl?: string;
}

function StoryItem({ 
  icon, 
  value, 
  label, 
  gradientFrom, 
  gradientTo, 
  ringColor,
  onClick,
  hasProgress,
  progressPercent = 0,
  isComplete,
  avatarUrl
}: StoryItemProps) {
  return (
    <button 
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 flex-shrink-0 group"
    >
      {/* Instagram-style ring with gradient */}
      <div 
        className="relative p-[3px] rounded-full transition-transform duration-200 group-active:scale-95"
        style={{
          background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`
        }}
      >
        {/* Inner white/dark gap ring */}
        <div className="p-[2px] rounded-full bg-background">
          {/* Story circle content */}
          <div className="relative w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-full overflow-hidden bg-black/40 backdrop-blur-sm flex items-center justify-center">
            {/* Blurred background avatar */}
            {avatarUrl && (
              <>
                <div 
                  className="absolute inset-0 bg-cover bg-center scale-125"
                  style={{ 
                    backgroundImage: `url(${avatarUrl})`,
                    filter: 'blur(2px) saturate(1.2)',
                    opacity: 0.5
                  }}
                />
                <div className="absolute inset-0 bg-black/30" />
              </>
            )}
            
            {/* Progress fill for progress widget */}
            {hasProgress && (
              <div 
                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-purple-500/50 via-violet-400/30 to-transparent transition-all duration-700"
                style={{ height: `${progressPercent}%` }}
              />
            )}
            
            {/* Content */}
            <div className="relative z-10 flex flex-col items-center justify-center">
              <div className="mb-0.5">{icon}</div>
              <span className="text-sm sm:text-base md:text-lg font-bold text-white font-display drop-shadow-md">
                {value}
              </span>
            </div>
            
            {/* Complete checkmark */}
            {isComplete && (
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-background z-20">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Label */}
      <span className="text-[10px] sm:text-xs text-white/70 font-medium tracking-wide">
        {label}
      </span>
    </button>
  );
}

export function ReadingJourneyStories({
  currentStreak,
  totalBooksRead,
  readingGoal,
  booksThisMonth = 0,
  isLoading = false,
  isError = false,
  refetchData,
  onOpenAuthModal,
  updateReadingGoal,
  isUpdatingGoal = false
}: ReadingJourneyStoriesProps) {
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const { creditBalance, isLoading: isCreditsLoading } = useProfileCredits();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { data: appConfig } = useAppConfig();
  const { data: readingData } = useProfileReadingData();
  const { data: listeningStats } = useCombinedListeningStats();
  const { avatars: bookAvatars } = useBookAvatars();
  const userId = session?.user?.id;

  // Generate stable random avatars for each story circle
  const storyAvatars = useMemo(() => {
    if (bookAvatars.length === 0) return {};
    const getRandomAvatar = (seed: number) => {
      const index = seed % bookAvatars.length;
      return bookAvatars[index]?.image_url;
    };
    return {
      streak: getRandomAvatar(1),
      books: getRandomAvatar(2),
      goal: getRandomAvatar(3),
      progress: getRandomAvatar(4),
      consistency: getRandomAvatar(5),
      night: getRandomAvatar(6),
      scenes: getRandomAvatar(7),
      month: getRandomAvatar(8),
      listening: getRandomAvatar(9),
      favorite: getRandomAvatar(10),
      repeats: getRandomAvatar(11),
      likedTracks: getRandomAvatar(12),
    };
  }, [bookAvatars]);

  // Use default values when not signed in
  const displayStreak = session ? currentStreak : 0;
  const displayBooksRead = session ? totalBooksRead : 0;
  const displayReadingGoal = session ? readingGoal : 10;
  const displayBooksThisMonth = session ? booksThisMonth : 0;

  const isSubscriptionMode = appConfig?.current_model === 'subscription' || subscriptionStatus?.subscribed;
  const displayProgressPercentage = displayReadingGoal > 0 ? displayBooksRead / displayReadingGoal * 100 : 0;
  const [showDetailedView, setShowDetailedView] = useState(false);
  const { toast } = useToast();

  const fireIconColor = displayStreak > 1 ? "text-orange-400" : "text-orange-300";

  useEffect(() => {
    if (refetchData) {
      refetchData();
    }
  }, [refetchData]);

  // Goal celebration effect
  useEffect(() => {
    if (!session || !userId) return;
    
    const goalReached = displayProgressPercentage >= 100 && displayReadingGoal > 0;
    const celebrationKey = `goalCelebrated_${userId}_${displayReadingGoal}`;
    const hasAlreadyCelebrated = localStorage.getItem(celebrationKey);
    
    if (goalReached && !hasAlreadyCelebrated) {
      const duration = 2000;
      const animationEnd = Date.now() + duration;
      const colors = ['#34C759', '#007AFF', '#AF52DE'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors
        });

        if (Date.now() < animationEnd) {
          requestAnimationFrame(frame);
        }
      })();

      toast({
        title: "🎉 Goal Complete!",
        description: `You've read ${displayBooksRead} books!`,
        duration: 5000,
      });

      localStorage.setItem(celebrationKey, 'true');
    }
  }, [displayProgressPercentage, displayReadingGoal, displayBooksRead, userId, session, toast]);

  const handleStoryClick = () => {
    if (!session) {
      onOpenAuthModal?.();
      return;
    }
    setShowDetailedView(true);
  };

  if (isError) {
    return null;
  }

  const isLoadingState = session && (isLoading || isCreditsLoading);

  return (
    <>
      <div className="w-full">
        {/* Section header */}
        <div className="flex items-center justify-between px-4 lg:px-12 xl:px-16 mb-3 lg:justify-center lg:gap-4 relative">
          <h3 className="text-sm font-semibold text-white/80 tracking-wide uppercase">
            Your Journey
          </h3>
          {session && (
            <button 
              onClick={handleStoryClick}
              className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors lg:absolute lg:right-12 xl:right-16"
            >
              View All
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        
        {/* Horizontal scroll container */}
        <div className="overflow-x-auto scrollbar-hide lg:overflow-visible">
          <div className="flex items-start gap-4 px-4 pb-2 lg:justify-center lg:px-12 xl:px-16">
            {isLoadingState ? (
              // Loading skeletons
              <>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5 flex-shrink-0">
                    <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-full bg-white/10 animate-pulse" />
                    <div className="w-10 h-2 rounded bg-white/10 animate-pulse" />
                  </div>
                ))}
              </>
            ) : (
              <>
                {/* Profile Story - Avatar with name */}
                <button 
                  onClick={handleStoryClick}
                  className="flex flex-col items-center gap-1.5 flex-shrink-0 group"
                >
                  <div 
                    className="relative p-[3px] rounded-full transition-transform duration-200 group-active:scale-95"
                    style={{
                      background: session && currentProfile?.avatar_url 
                        ? 'linear-gradient(135deg, #833AB4, #FD1D1D, #FCB045)' 
                        : 'linear-gradient(135deg, #374151, #1F2937)'
                    }}
                  >
                    <div className="p-[2px] rounded-full bg-background">
                      <div className="relative w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-full overflow-hidden bg-black/40 backdrop-blur-sm flex items-center justify-center">
                        {currentProfile?.avatar_url ? (
                          <img 
                            src={currentProfile.avatar_url} 
                            alt="Profile" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl">👤</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] sm:text-xs text-white/70 font-medium tracking-wide max-w-[60px] truncate">
                    {session ? (currentProfile?.name || 'You') : 'Sign In'}
                  </span>
                </button>

                {/* Streak Story */}
                <StoryItem
                  icon={<Flame className={`w-5 h-5 sm:w-6 sm:h-6 ${fireIconColor}`} />}
                  value={displayStreak}
                  label="Streak"
                  gradientFrom="#FF6B00"
                  gradientTo="#FF0844"
                  ringColor="orange"
                  onClick={handleStoryClick}
                  avatarUrl={storyAvatars.streak}
                />

                {/* Books Read Story */}
                <StoryItem
                  icon={<BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />}
                  value={displayBooksRead}
                  label="Books"
                  gradientFrom="#00C6FF"
                  gradientTo="#0072FF"
                  ringColor="blue"
                  onClick={handleStoryClick}
                  avatarUrl={storyAvatars.books}
                />

                {/* Goal Story */}
                <StoryItem
                  icon={<Target className={`w-5 h-5 sm:w-6 sm:h-6 ${displayProgressPercentage >= 100 ? 'text-yellow-400' : 'text-green-400'}`} />}
                  value={displayReadingGoal}
                  label="Goal"
                  gradientFrom={displayProgressPercentage >= 100 ? '#FFD700' : '#00F260'}
                  gradientTo={displayProgressPercentage >= 100 ? '#FF8C00' : '#0575E6'}
                  ringColor={displayProgressPercentage >= 100 ? 'yellow' : 'green'}
                  onClick={handleStoryClick}
                  avatarUrl={storyAvatars.goal}
                />

                {/* Progress Story */}
                <StoryItem
                  icon={<Book className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />}
                  value={`${Math.round(displayProgressPercentage)}%`}
                  label="Progress"
                  gradientFrom="#8E2DE2"
                  gradientTo="#F953C6"
                  ringColor="purple"
                  onClick={handleStoryClick}
                  hasProgress
                  progressPercent={displayProgressPercentage}
                  avatarUrl={storyAvatars.progress}
                />

                {/* 7-Day Consistency Story */}
                <StoryItem
                  icon={<TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />}
                  value={`${Math.round(readingData.readingConsistency7d)}%`}
                  label="7-Day"
                  gradientFrom="#11998E"
                  gradientTo="#38EF7D"
                  ringColor="green"
                  onClick={handleStoryClick}
                  hasProgress
                  progressPercent={readingData.readingConsistency7d}
                  avatarUrl={storyAvatars.consistency}
                />

                {/* Night Reads Story */}
                <StoryItem
                  icon={<Moon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-300" />}
                  value={readingData.nightReadsThisWeek}
                  label="Night"
                  gradientFrom="#4A00E0"
                  gradientTo="#8E2DE2"
                  ringColor="indigo"
                  onClick={handleStoryClick}
                  avatarUrl={storyAvatars.night}
                />

                {/* Scenes Story */}
                <StoryItem
                  icon={<Eye className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400" />}
                  value={readingData.scenesExperienced > 999 ? `${Math.round(readingData.scenesExperienced / 1000)}k` : readingData.scenesExperienced}
                  label="Scenes"
                  gradientFrom="#F857A6"
                  gradientTo="#FF5858"
                  ringColor="pink"
                  onClick={handleStoryClick}
                  avatarUrl={storyAvatars.scenes}
                />

                {/* Listening Time Story */}
                <StoryItem
                  icon={<Headphones className="w-5 h-5 sm:w-6 sm:h-6 text-teal-400" />}
                  value={listeningStats.totalListeningMinutes > 999 ? `${Math.round(listeningStats.totalListeningMinutes / 60)}h` : `${listeningStats.totalListeningMinutes}m`}
                  label="Listened"
                  gradientFrom="#14B8A6"
                  gradientTo="#06B6D4"
                  ringColor="teal"
                  onClick={handleStoryClick}
                  avatarUrl={storyAvatars.listening}
                />

                {/* Favorite Book Story */}
                {readingData.favoriteBook && (
                  <StoryItem
                    icon={<Heart className="w-5 h-5 sm:w-6 sm:h-6 text-red-400" />}
                    value={readingData.mostRepeats}
                    label="Favorite"
                    gradientFrom="#EF4444"
                    gradientTo="#F97316"
                    ringColor="red"
                    onClick={handleStoryClick}
                    avatarUrl={storyAvatars.favorite}
                  />
                )}

                {/* Liked Tracks Story */}
                <StoryItem
                  icon={<Music className="w-5 h-5 sm:w-6 sm:h-6 text-pink-400" />}
                  value={listeningStats.likedTracksCount}
                  label="Tracks"
                  gradientFrom="#EC4899"
                  gradientTo="#A855F7"
                  ringColor="pink"
                  onClick={handleStoryClick}
                  avatarUrl={storyAvatars.likedTracks}
                />

                {/* Monthly Books Story (subscription mode) */}
                {isSubscriptionMode && (
                  <StoryItem
                    icon={<Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />}
                    value={displayBooksThisMonth}
                    label="This Month"
                    gradientFrom="#00D2FF"
                    gradientTo="#3A7BD5"
                    ringColor="cyan"
                    onClick={handleStoryClick}
                    avatarUrl={storyAvatars.month}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Reading Journey Modal */}
      <ReadingJourneyModal 
        isOpen={showDetailedView} 
        onClose={() => setShowDetailedView(false)} 
        currentStreak={currentStreak} 
        totalBooksRead={totalBooksRead} 
        readingGoal={readingGoal} 
        booksThisMonth={displayBooksThisMonth}
        onUpdateGoal={updateReadingGoal || (() => {})}
        readingConsistency7d={readingData.readingConsistency7d}
        sessionsToday={readingData.sessionsToday}
        totalReadingTime={readingData.totalReadingTime}
        longestSession={readingData.longestSession}
        nightReadsThisWeek={readingData.nightReadsThisWeek}
        scenesExperienced={readingData.scenesExperienced}
        readingPace={readingData.readingPace}
        favoriteBook={readingData.favoriteBook}
        mostRepeats={readingData.mostRepeats}
        totalListeningMinutes={listeningStats.totalListeningMinutes}
        likedTracksCount={listeningStats.likedTracksCount}
        likedAudiobooksCount={listeningStats.likedAudiobooksCount}
      />
    </>
  );
}
